import { useEffect } from "react";
import { useImmer } from "use-immer";
import { errorMessage, successMessage } from "../../../utils";
import {
  getTenantAndroidSettingsApi,
  getTenantIosSettingsApi,
  updateTenantAndroidSettingsApi,
  updateTenantIosSettingsApi,
} from "../apis/appPlatformSettingsApis";

const initialFormData = {
  store_url: "",
  store_version: "",
  force_update_current_version: "",
  title: "",
  message: "",
  force_update_required: false,
};

const platformApis = {
  android: {
    get: getTenantAndroidSettingsApi,
    update: updateTenantAndroidSettingsApi,
  },
  ios: {
    get: getTenantIosSettingsApi,
    update: updateTenantIosSettingsApi,
  },
};

const normalizeSettings = (settings = {}) => ({
  ...initialFormData,
  ...settings,
  force_update_required: Boolean(Number(settings.force_update_required)),
});

export const useAppPlatformSettings = ({ platform }) => {
  const [state, setState] = useImmer({
    formData: initialFormData,
    isLoading: true,
    isSubmitting: false,
    hasChanges: false,
  });
  const api = platformApis[platform];

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get();
        if (!response?.success) {
          throw new Error(
            response?.message || `Failed to load ${platform} settings`,
          );
        }

        setState((draft) => {
          draft.formData = normalizeSettings(response.data);
        });
      } catch (error) {
        errorMessage(
          error.response?.data?.message ||
            error.message ||
            `Failed to load ${platform} settings`,
        );
      } finally {
        setState((draft) => {
          draft.isLoading = false;
        });
      }
    };

    fetchSettings();
  }, [api, platform, setState]);

  const onChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.formData[name] = value;
      draft.hasChanges = true;
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    try {
      setState((draft) => {
        draft.isSubmitting = true;
      });

      const payload = {
        ...state.formData,
        force_update_required: Number(state.formData.force_update_required),
      };
      const response = await api.update(payload);
      if (!response?.success) {
        throw new Error(
          response?.message || `Failed to update ${platform} settings`,
        );
      }

      setState((draft) => {
        draft.formData = normalizeSettings(response.data || payload);
        draft.hasChanges = false;
      });
      successMessage(
        response.message || `${platform} settings updated successfully`,
      );
    } catch (error) {
      errorMessage(
        error.response?.data?.message ||
          error.message ||
          `Failed to update ${platform} settings`,
      );
    } finally {
      setState((draft) => {
        draft.isSubmitting = false;
      });
    }
  };

  return { state, onChange, onSubmit };
};
